import React, { useEffect, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Box, Button, TextField, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { COLORS, FONT } from "../../constants";
import { sanitizeDigits } from "../../utils/helpers";

const rowInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "2px",
    "& fieldset": { borderColor: "#d0d0d0" },
    "&:hover fieldset": { borderColor: COLORS.themeBlack },
    "&.Mui-focused fieldset": { borderColor: COLORS.themeBlack, borderWidth: 1 },
  },
  "& .MuiInputBase-input": { fontSize: FONT.size.xs, py: "6px" },
};

const SuretyRow = ({ index, required = false, name, idNo, sig, onNameChange, onIdChange, onSave, onClear }) => {
  const sigRef = useRef(null);
  const containerRef = useRef(null);

  const prevHeight = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const canvas = sigRef.current?.getCanvas();
      if (!canvas) return;
      const { width, height } = entry.contentRect;
      const newHeight = Math.round(height);
      if (prevHeight.current !== null && prevHeight.current !== newHeight) {
        sigRef.current?.clear();
        onClear();
      }
      prevHeight.current = newHeight;
      canvas.width = Math.round(width);
      canvas.height = newHeight;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [onClear]);

  const handleSave = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      onSave(sigRef.current.toDataURL("image/png"));
    }
  };

  const handleClear = () => {
    sigRef.current?.clear();
    onClear();
  };

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
      <Typography sx={{ fontSize: FONT.size.xs, fontWeight: 700, minWidth: 14, pt: "22px" }}>
        {index}.
      </Typography>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: "0.65rem", color: COLORS.lightText, mb: 0.5, letterSpacing: 0.4 }}>
            FULL NAME
            {required && <Box component="span" sx={{ color: COLORS.themeRed, ml: 0.25 }}>*</Box>}
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            sx={rowInputSx}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: "0.65rem", color: COLORS.lightText, mb: 0.5, letterSpacing: 0.4 }}>
            ID NO.
            {required && <Box component="span" sx={{ color: COLORS.themeRed, ml: 0.25 }}>*</Box>}
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={idNo}
            onChange={(e) => onIdChange(sanitizeDigits(e.target.value))}
            sx={rowInputSx}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: "0.65rem", color: COLORS.lightText, mb: 0.5, letterSpacing: 0.4 }}>
            SIGNATURE
            {required && <Box component="span" sx={{ color: COLORS.themeRed, ml: 0.25 }}>*</Box>}
          </Typography>
          <Box
            ref={containerRef}
            sx={{
              border: `1px solid ${sig ? COLORS.themeRed : "#d0d0d0"}`,
              borderRadius: "4px",
              backgroundColor: COLORS.white,
              lineHeight: 0,
              transition: "border-color 0.2s",
              overflow: "hidden",
              resize: "vertical",
              height: "72px",
              minHeight: "52px",
            }}
          >
            {sig ? (
              <Box
                component="img"
                src={sig}
                alt="Saved signature"
                sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
              />
            ) : (
              <SignatureCanvas
                ref={sigRef}
                penColor="#1a1a1a"
                backgroundColor="rgb(255,255,255)"
                canvasProps={{ style: { width: "100%", height: "100%", display: "block", cursor: "crosshair" } }}
              />
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Button
              onClick={handleClear}
              size="small"
              sx={{
                fontSize: "0.65rem",
                px: 1.25,
                py: 0.25,
                minWidth: 0,
                border: "1px solid #d0d0d0",
                borderRadius: "2px",
                color: COLORS.darkText,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { borderColor: COLORS.themeBlack, backgroundColor: "transparent" },
              }}
            >
              Clear
            </Button>
            <Button
              onClick={handleSave}
              disabled={!!sig}
              size="small"
              sx={{
                fontSize: "0.65rem",
                px: 1.25,
                py: 0.25,
                minWidth: 0,
                backgroundColor: COLORS.themeBlack,
                color: COLORS.white,
                borderRadius: "2px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { backgroundColor: COLORS.themeBlack, boxShadow: "none" },
                "&.Mui-disabled": { backgroundColor: "#cccccc", color: COLORS.white },
              }}
            >
              Save
            </Button>
            {sig && <CheckCircleOutlineIcon sx={{ fontSize: 14, color: "#2e7d32" }} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SuretyRow;
